import React from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { ChatState, ChatMessage, Conversation, isCardMessage, CardMessage, MessageBase } from '@/features/chat/types/chat';
import type {
  LexAgentClarification,
  LexAgentAction,
  LexAgentState,
  LexAgentPlugin,
  LexAgentTelemetry,
  LexAgentCost,
} from '@/types/lexAgent';

// Default conversation to start with when none exists
const DEFAULT_CONVERSATION_TITLE = 'New Chat';

// Helper to create a new message


// Helper to create a new conversation
const createConversation = (
  title: string = DEFAULT_CONVERSATION_TITLE,
  id: string = uuidv4()
): Conversation => ({
  id,
  title,
  messages: [],
  createdAt: new Date(),
  updatedAt: new Date(),
});

// Create the store with persistence
export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      currentConversationId: null,
      conversations: {},
      isSidebarOpen: true,
      isLoading: false,
      error: null,

      // Intention summary state
      currentIntent: null,
      intentConfidence: null,
      fallbackStrategy: null,
      followUpQueue: [],

      // New fields for agent integration
      clarification: null as LexAgentClarification | null,
      actions: [] as LexAgentAction[],
      agentState: null as LexAgentState | null,
      plugins: [] as LexAgentPlugin[],
      telemetry: null as LexAgentTelemetry | null,
      cost: null as LexAgentCost | null,
      // Set clarification prompt from agent
      setClarification: (clarification: LexAgentClarification | null) => set({ clarification }),

      // Set agent-suggested actions
      setActions: (actions: LexAgentAction[]) => set({ actions }),

      // Set agent state from /state endpoint
      setAgentState: (agentState: LexAgentState | null) => set({ agentState }),


      // Set available plugins
      setPlugins: (plugins: LexAgentPlugin[]) => set({ plugins }),

      // Track active plugin IDs for the current chat session
      activePluginIds: [],
      setActivePluginIds: (ids) => set({ activePluginIds: ids }),
      togglePlugin: (pluginId) => set((state) => {
        const isActive = state.activePluginIds.includes(pluginId);
        return {
          activePluginIds: isActive
            ? state.activePluginIds.filter((id: string) => id !== pluginId)
            : [...state.activePluginIds, pluginId],
        };
      }),

      // Set telemetry/cost info
      setTelemetry: (telemetry: LexAgentTelemetry | null) => set({ telemetry }),
      setCost: (cost: LexAgentCost | null) => set({ cost }),
      // Set intention summary (to be called from ChatView or MCP response)
      setIntentionSummary: (intent, confidence, fallback, followUps) => {
        set(() => ({
          currentIntent: intent,
          intentConfidence: confidence,
          fallbackStrategy: fallback,
          followUpQueue: followUps || [],
        }));
      },

      // Create a new conversation and set it as current
      createConversation: (title, preferredId) => {
        const desiredId = preferredId && preferredId.trim() ? preferredId.trim() : uuidv4();

        if (get().conversations[desiredId]) {
          set({ currentConversationId: desiredId });
          return desiredId;
        }

        const newConversation = createConversation(title || DEFAULT_CONVERSATION_TITLE, desiredId);
        set((state) => ({
          conversations: {
            ...state.conversations,
            [newConversation.id]: newConversation,
          },
          currentConversationId: newConversation.id,
        }));
        return newConversation.id;
      },

      // Delete a conversation
      deleteConversation: (id) => {
        set((state) => {
          const newConversations = { ...state.conversations };
          delete newConversations[id];
          
          // If we're deleting the current conversation, switch to another one or create a new one
          let currentId = state.currentConversationId;
          if (currentId === id) {
            const remainingIds = Object.keys(newConversations);
            currentId = remainingIds.length > 0 ? remainingIds[0] : get().createConversation();
          }

          return {
            conversations: newConversations,
            currentConversationId: currentId,
          };
        });
      },

      // Set the current conversation
      setCurrentConversation: (id) => {
        if (get().conversations[id]) {
          set({ currentConversationId: id });
        }
      },

      // Add a message to the current or specified conversation
      addMessage: (message, conversationId) => {
        const targetId = conversationId || get().currentConversationId;
        if (!targetId) {
          const newId = get().createConversation();
          return get().addMessage(message, newId);
        }

        const messageId = uuidv4();
        let newMessage: ChatMessage;
        if ((message as Partial<CardMessage>).cardType) {
          // CardMessage
          const cardMsg = message as Omit<CardMessage, 'id' | 'createdAt' | 'updatedAt'>;
          newMessage = {
            ...cardMsg,
            id: messageId,
            createdAt: new Date(),
            updatedAt: new Date(),
            status: message.status || 'sent',
            role: cardMsg.role === 'assistant' || cardMsg.role === 'system' ? cardMsg.role : 'assistant',
            cardType: cardMsg.cardType,
            content: cardMsg.content,
            renderType: 'card',
          };
        } else {
          // MessageBase
          const baseMsg = message as Omit<MessageBase, 'id' | 'createdAt' | 'updatedAt'>;
          newMessage = {
            ...baseMsg,
            id: messageId,
            createdAt: new Date(),
            updatedAt: new Date(),
            status: message.status || 'sent',
            role: baseMsg.role,
            content: baseMsg.content,
            renderType: baseMsg.renderType,
            name: baseMsg.name,
            error: baseMsg.error,
            intentCategory: baseMsg.intentCategory,
            streamId: baseMsg.streamId,
            metadata: baseMsg.metadata,
          };
        }

        set((state) => {
          const conversation = state.conversations[targetId];
          if (!conversation) return state;

          return {
            conversations: {
              ...state.conversations,
              [targetId]: {
                ...conversation,
                messages: [...conversation.messages, newMessage],
                updatedAt: new Date(),
              },
            },
          };
        });

        return messageId;
      },

      // Update an existing message
      updateMessage: (messageId, updates, conversationId) => {
        const targetId = conversationId || get().currentConversationId;
        if (!targetId) return;

        set((state) => {
          const conversation = state.conversations[targetId];
          if (!conversation) return state;

          const messageIndex = conversation.messages.findIndex((m) => m.id === messageId);
          if (messageIndex === -1) return state;

          const updatedMessages = [...conversation.messages];
          const prev = updatedMessages[messageIndex];
          if ((prev as Partial<CardMessage>).cardType) {
            // CardMessage update
            const cardUpdates = updates as Partial<CardMessage>;
            updatedMessages[messageIndex] = {
              ...prev,
              ...cardUpdates,
              updatedAt: new Date(),
              role: (prev as CardMessage).role === 'assistant' || (prev as CardMessage).role === 'system' ? (prev as CardMessage).role : 'assistant',
              cardType: cardUpdates.cardType ?? (prev as CardMessage).cardType,
              content: cardUpdates.content ?? (prev as CardMessage).content,
              renderType: 'card',
            };
          } else {
            // MessageBase update
            const baseUpdates = updates as Partial<MessageBase>;
            updatedMessages[messageIndex] = {
              ...prev,
              ...baseUpdates,
              updatedAt: new Date(),
              role: (baseUpdates.role ?? (prev as MessageBase).role),
              content: (baseUpdates.content ?? (prev as MessageBase).content),
              renderType: (baseUpdates.renderType ?? (prev as MessageBase).renderType),
              name: (baseUpdates.name ?? (prev as MessageBase).name),
              error: (baseUpdates.error ?? (prev as MessageBase).error),
              intentCategory: (baseUpdates.intentCategory ?? (prev as MessageBase).intentCategory),
              streamId: (baseUpdates.streamId ?? (prev as MessageBase).streamId),
              metadata: (baseUpdates.metadata ?? (prev as MessageBase).metadata),
            };
          }

          return {
            conversations: {
              ...state.conversations,
              [targetId]: {
                ...conversation,
                messages: updatedMessages,
                updatedAt: new Date(),
              },
            },
          };
        });
      },

      // Delete a message
      deleteMessage: (messageId, conversationId) => {
        const targetId = conversationId || get().currentConversationId;
        if (!targetId) return;

        set((state) => {
          const conversation = state.conversations[targetId];
          if (!conversation) return state;

          return {
            conversations: {
              ...state.conversations,
              [targetId]: {
                ...conversation,
                messages: conversation.messages.filter((m) => m.id !== messageId),
                updatedAt: new Date(),
              },
            },
          };
        });
      },

      // Clear all messages in a conversation
      clearConversation: (conversationId) => {
        const targetId = conversationId || get().currentConversationId;
        if (!targetId) return;

        set((state) => {
          const conversation = state.conversations[targetId];
          if (!conversation) return state;

          return {
            conversations: {
              ...state.conversations,
              [targetId]: {
                ...conversation,
                messages: [],
                updatedAt: new Date(),
              },
            },
          };
        });
      },

      // Toggle sidebar visibility
      toggleSidebar: () => {
        set((state) => ({
          isSidebarOpen: !state.isSidebarOpen,
        }));
      },

      // Send a card message
      sendCardMessage: (cardType, content, conversationId) => {
        const message: Omit<import('@/features/chat/types/chat').CardMessage, 'id' | 'createdAt' | 'updatedAt'> = {
          role: 'assistant',
          content: { type: cardType, ...content },
          cardType,
          renderType: 'card',
          status: 'sent',
          metadata: { isCard: true },
        };
        return get().addMessage(message, conversationId);
      },

      // Update card content
      updateCardContent: (messageId, updates, conversationId) => {
        const targetId = conversationId || get().currentConversationId;
        if (!targetId) return;

        set((state) => {
          const conversation = state.conversations[targetId];
          if (!conversation) return state;

          const messageIndex = conversation.messages.findIndex((m) => m.id === messageId);
          if (messageIndex === -1 || !isCardMessage(conversation.messages[messageIndex])) return state;

          const updatedMessages = [...conversation.messages];
          const currentMessage = updatedMessages[messageIndex];
          if (isCardMessage(currentMessage)) {
            updatedMessages[messageIndex] = {
              ...currentMessage,
              content: {
                ...currentMessage.content,
                ...updates,
              },
              updatedAt: new Date(),
            };
          }

          return {
            conversations: {
              ...state.conversations,
              [targetId]: {
                ...conversation,
                messages: updatedMessages,
                updatedAt: new Date(),
              },
            },
          };
        });
      },

      // Load conversations from storage (called by persist middleware)
      loadConversations: async () => {
        // This is a no-op as Zustand's persist middleware handles the loading
      },

      // Save conversations to storage (called by persist middleware)
      saveConversations: async () => {
        // This is a no-op as Zustand's persist middleware handles the saving
      },
    }),
    {
      name: 'chat-storage', // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
      partialize: (state) => ({
        conversations: state.conversations,
        currentConversationId: state.currentConversationId,
        isSidebarOpen: state.isSidebarOpen,
        clarification: state.clarification,
        actions: state.actions,
        agentState: state.agentState,
        plugins: state.plugins,
        telemetry: state.telemetry,
        cost: state.cost,
      }),
    }
  )
);

// Initialize with a default conversation if none exists
const initializeStore = () => {
  // Intentionally no-op: ChatView bootstraps a deterministic conversation ID
  // (`user-{keycloakSub}-documents-active`) after authentication.
};

// Export a hook that initializes the store when used
export const useInitializedChatStore = () => {
  const store = useChatStore();
  React.useEffect(() => {
    initializeStore();
  }, []);
  return store;
};

// Export store actions for direct access when needed
export const chatStoreActions = {
  // Add any direct store actions here if needed
};
